import React from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { useAuth } from "@/lib/auth";
import { ArrowLeft, ShieldAlert, AlertTriangle, CheckCircle2, Mail, Send, RefreshCcw, Sparkles } from "lucide-react";

/**
 * Business Matchmaking Runbook — a static, printable operating document
 * that walks the Display America team through building, validating, and
 * publishing the Sept 11 matchmaking schedule.
 *
 * Route: /admin/matchmaking/runbook
 */
export default function MatchmakingRunbook() {
  const { user, loading } = useAuth();
  const nav = useNavigate();

  React.useEffect(() => {
    if (loading) return;
    if (!user || (user.role !== "admin" && user.role !== "tl_internal")) nav("/");
  }, [user, loading, nav]);

  return (
    <Layout>
      <section className="border-b border-[hsl(var(--line))]">
        <div className="container-summit py-10 max-w-4xl">
          <Link to="/admin/matchmaking" className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 hover:text-black inline-flex items-center gap-1" data-testid="rb-back">
            <ArrowLeft size={12} /> Matchmaking Console
          </Link>
          <div className="mt-4">
            <div className="overline"><span className="accent-rule" />Display America · Ops Runbook</div>
            <h1 className="text-4xl sm:text-5xl mt-3">Business Matchmaking Runbook</h1>
            <p className="text-sm text-zinc-600 max-w-2xl mt-4 leading-relaxed">
              Step-by-step operating procedure for building, validating, and publishing the September 11 Business Matchmaking schedule. Read top-to-bottom the first time; use the section links to jump back during ops.
            </p>
            <div className="mt-6 flex flex-wrap gap-3 text-[11px] uppercase tracking-[0.16em]">
              <button onClick={() => window.print()} className="border border-zinc-300 hover:border-black px-3 py-2" data-testid="rb-print">Print / Save as PDF</button>
              <Link to="/admin/matchmaking" className="border border-black bg-black text-white hover:bg-zinc-800 px-3 py-2" data-testid="rb-open-console">Open the Console →</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="container-summit py-12 max-w-4xl grid gap-10">
        {/* At a glance */}
        <Block id="at-a-glance" label="At a glance · Sept 11, 2026" testId="rb-glance">
          <ul className="grid sm:grid-cols-2 gap-3 text-sm text-zinc-700">
            <Fact label="Window">1:00 PM – 3:30 PM ET</Fact>
            <Fact label="Meeting duration">15 minutes</Fact>
            <Fact label="Total time slots">10</Fact>
            <Fact label="Formal Business Matchmaking">Slots 1–8 (1:00 – 3:00 PM)</Fact>
            <Fact label="Open Collaboration &amp; Networking">Slots 9–10 (3:00 – 3:30 PM)</Fact>
            <Fact label="Tables">M-1 through M-8 (fixed; participants rotate)</Fact>
            <Fact label="Per-Prime meeting cap">8 (one per formal slot)</Fact>
            <Fact label="Fair-participation target">3 meetings per participant</Fact>
          </ul>
          <div className="text-xs text-zinc-500 mt-4">A single appointment = 1 Prime × 1 SBE × 1 table × 1 time slot.</div>
        </Block>

        {/* Roles */}
        <Block id="roles" label="Who does what" testId="rb-roles">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 text-left">
                <th className="py-2 pr-4">Role</th>
                <th className="py-2 pr-4">Responsibilities</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              <RoleRow role="Display America Admin" text="Owns the entire schedule. Reviews interests, builds meetings in the Matchmaking Console, runs the pre-publish guardrail, publishes, and monitors the notification log." />
              <RoleRow role="Travel + Leisure Sourcing" text="Reviews the Prime Supplier Matchmaking Brief link, curates which SBEs to invite, and shares direction with Display America before scheduling begins." />
              <RoleRow role="Prime Suppliers" text="Register, complete their company profile, and star SBEs they want to meet. Both the Primary and Additional Representative share ONE company schedule." />
              <RoleRow role="Small Business Enterprises" text="Register, complete their profile, and star Primes they want to pitch. Mutual interest becomes a scheduled meeting." />
            </tbody>
          </table>
        </Block>

        {/* Pre-flight */}
        <Block id="preflight" label="Step 1 · Pre-flight checklist" testId="rb-preflight">
          <ol className="list-decimal pl-5 grid gap-3 text-sm text-zinc-700 marker:text-zinc-400 marker:font-medium">
            <li>
              Confirm all invited <strong>Prime Suppliers</strong> are registered and marked <code className="text-[11px] px-1 py-0.5 bg-zinc-100 rounded">approved</code> in <Link to="/admin" className="underline underline-offset-2">Admin → Exhibitors</Link>.
            </li>
            <li>
              Confirm all invited <strong>SBEs</strong> have registered via the SBE registration flow. Each SBE should have a completed profile (industries served, sourcing preferences, capability statement).
            </li>
            <li>
              Ask <strong>each Prime</strong> to browse <code className="text-[11px] px-1 py-0.5 bg-zinc-100 rounded">/matchmaking</code> and star the SBEs they want to meet.
            </li>
            <li>
              Ask <strong>each SBE</strong> to do the same — star the Primes they want to pitch.
            </li>
            <li>
              Share the Travel + Leisure Prime Brief link from <Link to="/admin" className="underline underline-offset-2">Admin → Matchmaking tab</Link> with the T+L sourcing team so they can review Prime profiles + matchmaking interests. This link intentionally hides Prime PII (name, email, phone).
            </li>
          </ol>
        </Block>

        {/* Build meetings */}
        <Block id="build" label="Step 2 · Build the meetings" testId="rb-build">
          <p className="text-sm text-zinc-700 mb-4">
            Head to <Link to="/admin/matchmaking" className="underline underline-offset-2 font-medium">Matchmaking Console</Link>. Two workflows are available:
          </p>
          <Sub label="A · Prime Focus (recommended)">
            <ol className="list-decimal pl-5 grid gap-2 text-sm text-zinc-700 marker:text-zinc-400">
              <li>Open the <em>Prime Focus · Ranked SBE Suggestions</em> panel and select a Prime Supplier.</li>
              <li>Read the Prime overview: <em>Scheduled / Allowance / Open Slots / Available Formal Time Slots</em>.</li>
              <li>
                Review the ranked SBE list. Labels appear on each row:
                <div className="mt-2 flex flex-wrap gap-2 text-[10px] uppercase tracking-[0.16em]">
                  <Chip color="emerald">Mutual Interest</Chip>
                  <Chip color="amber">Prime Interested</Chip>
                  <Chip color="amber">SBE Interested</Chip>
                  <Chip color="zinc">Recommended</Chip>
                </div>
                <div className="text-xs text-zinc-500 mt-2">
                  Mutual Interest first (both sides starred each other), then Prime→ or SBE→ one-way stars, then Recommended (strong capability overlap even without stars). The list also nudges under-scheduled SBEs upward for fairness.
                </div>
              </li>
              <li>Click <strong>Assign</strong> on the SBE you want. The Cell Editor drawer opens pre-populated with the Prime + SBE at the next available formal slot on the first empty table.</li>
              <li>Review notes and hit <strong>Schedule Meeting</strong>. The meeting saves as a <em>draft</em>.</li>
              <li>Repeat until this Prime hits their meeting target (default 3, cap 8).</li>
              <li>Move to the next Prime and repeat.</li>
            </ol>
          </Sub>
          <Sub label="B · Grid direct assign">
            <ol className="list-decimal pl-5 grid gap-2 text-sm text-zinc-700 marker:text-zinc-400">
              <li>Click any empty cell in the 8-table × 10-slot grid.</li>
              <li>Pick a Prime from the dropdown, then pick an SBE from the filtered list (mutual-interest SBEs appear first, tagged &ldquo;Mutual&rdquo;).</li>
              <li>Add optional internal notes and hit <strong>Schedule Meeting</strong>.</li>
              <li>The system automatically blocks double-booking of the same Prime, SBE, or table within the same time slot (409 conflict).</li>
            </ol>
          </Sub>
          <Sub label="C · Auto-fill (optional starting point)">
            <p className="text-sm text-zinc-700">
              The <strong>Auto-fill from Mutual Interest</strong> button greedily creates draft meetings from every unscheduled mutual-interest pair, only into empty <em>formal</em> cells. Open Collaboration slots are always skipped. Great for a quick starting draft; still review each row.
            </p>
          </Sub>
          <Callout kind="info">
            <strong>Open Collaboration &amp; Networking (Slots 9–10, 3:00–3:30 PM):</strong> deliberately reserved for SBE-to-SBE conversations, executive introductions, informal networking, or schedule recovery. You <em>may</em> manually place an overflow meeting into an open slot if truly necessary, but the guardrail will flag it as an advisory before publish.
          </Callout>
        </Block>

        {/* Guardrail */}
        <Block id="guardrail" label="Step 3 · Run the pre-publish guardrail" testId="rb-guardrail">
          <p className="text-sm text-zinc-700 mb-4">
            When the schedule feels ready, click the <strong>Publish</strong> button on the Console. Instead of publishing immediately, the <em>Pre-Publish Guardrail</em> drawer opens with a full validation summary.
          </p>
          <Sub label="Hard conflicts — publish is BLOCKED">
            <div className="flex items-start gap-3 border border-red-200 bg-red-50/40 p-4">
              <ShieldAlert size={18} className="text-red-700 shrink-0 mt-0.5" />
              <div className="text-sm text-zinc-800">
                <div className="font-medium">Must be fixed before you can publish:</div>
                <ul className="list-disc pl-5 mt-2 space-y-1 text-zinc-700">
                  <li><strong>Participant double-booking</strong> — same Prime or SBE scheduled in two places at the same time.</li>
                  <li><strong>Table double-booking</strong> — two meetings at the same (table, time slot).</li>
                </ul>
                <div className="text-xs text-zinc-600 mt-2">
                  These should be almost impossible to hit — the assignment endpoint blocks them at save time. If one appears, edit or delete the offending appointment and re-run the guardrail.
                </div>
              </div>
            </div>
          </Sub>
          <Sub label="Advisories — you can proceed after ticking the acknowledgment box">
            <div className="flex items-start gap-3 border border-amber-200 bg-amber-50/40 p-4">
              <AlertTriangle size={18} className="text-amber-700 shrink-0 mt-0.5" />
              <div className="text-sm text-zinc-800">
                <ul className="list-disc pl-5 space-y-1 text-zinc-700">
                  <li><strong>Primes with zero meetings</strong> — highly visible so you can decide if this is intentional. Not automatically a blocker.</li>
                  <li><strong>SBEs with zero meetings</strong> — same handling.</li>
                  <li><strong>Below fair-participation target</strong> — participants scheduled for fewer than 3 meetings.</li>
                  <li><strong>Unassigned mutual matches</strong> — a Prime and SBE both starred each other but no meeting exists.</li>
                  <li><strong>Meetings in Open Collaboration slots</strong> — overflow placements. Confirm these are intentional.</li>
                </ul>
                <div className="text-xs text-zinc-600 mt-2">
                  Fix what you can, then tick <em>&ldquo;I&apos;ve reviewed the advisories above and want to publish anyway&rdquo;</em> to unlock the drawer&apos;s Publish button.
                </div>
              </div>
            </div>
          </Sub>
          <Sub label="All clear">
            <div className="flex items-start gap-3 border border-emerald-200 bg-emerald-50 p-4">
              <CheckCircle2 size={18} className="text-emerald-700 shrink-0 mt-0.5" />
              <div className="text-sm text-zinc-800">
                <div className="font-medium">No conflicts or advisories detected.</div>
                <div className="text-zinc-600 mt-1 text-xs">Safe to publish.</div>
              </div>
            </div>
          </Sub>
        </Block>

        {/* Publish */}
        <Block id="publish" label="Step 4 · Publish" testId="rb-publish">
          <ol className="list-decimal pl-5 grid gap-3 text-sm text-zinc-700 marker:text-zinc-400">
            <li>
              Click <strong>Publish</strong> inside the Guardrail Drawer. The Console returns immediately with a confirmation toast — the system does not block the admin request while emails send.
            </li>
            <li>
              Every draft meeting flips to <em>published</em>. Participants see their schedule on their Summit Dashboard the moment publish completes.
            </li>
            <li>
              Notification emails fire in the background. Each affected Prime primary contact, Additional Representative, and SBE receives:
              <div className="mt-2 border border-zinc-200 bg-zinc-50 p-3 text-[13px]">
                <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1">Subject</div>
                <div className="font-medium">Your Business Matchmaking Schedule is Ready</div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mt-3 mb-1">Body</div>
                <div className="text-zinc-700">
                  Your personalized Business Matchmaking schedule for the Travel + Leisure Strategic Supplier Summit is now available. Sign in to your Summit Dashboard to review your meeting times, participating companies, and assigned tables before September 11.
                </div>
              </div>
            </li>
            <li>
              A <strong>T-24h reminder</strong> is queued automatically. On Sept 10 the platform sends one reminder per participant with published appointments.
            </li>
          </ol>
          <Callout kind="warn">
            <strong>Need to fix a mistake after publish?</strong> Use <em>Roll Back to Draft</em> in the Console header — every published row flips back to draft. No new emails go out on roll-back. Edit, re-run the guardrail, and publish again.
          </Callout>
        </Block>

        {/* Notification log */}
        <Block id="notifications" label="Step 5 · Monitor the notification log" testId="rb-notifications">
          <p className="text-sm text-zinc-700 mb-3">
            After publish, scroll to the <strong>Notification Log</strong> panel below the grid.
          </p>
          <ul className="grid gap-2 text-sm text-zinc-700">
            <li><span className="inline-flex items-center gap-2 mr-1"><Mail size={12} className="text-zinc-500"/> </span>Every dispatched email appears with Recipient · Company · Role · Email · Kind (Publish or 24h) · Attempt · Sent at · Status.</li>
            <li><span className="text-emerald-700 font-medium">Sent</span> = SendGrid accepted the payload. That is the delivery signal we track for this event.</li>
            <li><span className="text-red-700 font-medium">Failed</span> = the send raised an error at dispatch (bad address, provider rejection). Hover the pill to see the reason.</li>
            <li>Filter by status (All / Sent / Failed) and kind (All / Publish / 24h Reminder).</li>
            <li>Click <strong>Resend</strong> on any failed row — the original row is preserved, a new attempt row is added. The counter increments.</li>
            <li>If several rows failed at once (e.g. a provider blip), use <strong>Resend All Failed</strong>. The system de-duplicates by (email + kind) so each participant only receives one retry per bulk action.</li>
          </ul>
          <Callout kind="info">
            Delivered / opened / bounced tracking (via SendGrid Event Webhooks) is scheduled for post-inaugural. For this event, the operational rule is: any <em>Failed</em> row is an active follow-up — resend, and if it fails again call or email the participant directly.
          </Callout>
        </Block>

        {/* Rollback */}
        <Block id="edits" label="Editing after publish" testId="rb-edits">
          <ul className="grid gap-2 text-sm text-zinc-700 list-disc pl-5 marker:text-zinc-400">
            <li>
              <strong>Small change (one meeting):</strong> click the cell in the grid, edit the Prime / SBE / notes, save. Status stays <em>published</em>. Participant itinerary reflects the change on their next dashboard load. Emails are NOT re-fired for a single edit — inform the affected participants directly if timing changed.
            </li>
            <li>
              <strong>Larger change (many meetings):</strong> click <strong>Roll Back to Draft</strong>. Everything returns to draft; itineraries hide until you publish again. Re-run the guardrail before the next publish.
            </li>
            <li>
              <strong>New draft added after publish:</strong> Publish again — only the newly-added drafts flip. Participants added in this second publish will receive their first &ldquo;Schedule is Ready&rdquo; email; previously-notified participants are NOT re-emailed.
            </li>
          </ul>
        </Block>

        {/* Day of */}
        <Block id="day-of" label="Sept 11 · Day-of operations" testId="rb-day-of">
          <ol className="list-decimal pl-5 grid gap-3 text-sm text-zinc-700 marker:text-zinc-400">
            <li>Confirm all participants have received their T-24h reminder in the Notification Log. Resend any Failed rows.</li>
            <li>At badge pickup, remind participants to open their Summit Dashboard. Meetings show as: <em>Time · Counterpart Company · Table M-{"{n}"}</em>.</li>
            <li>Business Matchmaking begins at 1:00 PM. Participants rotate to their assigned table at each slot boundary.</li>
            <li>Between Slot 8 (2:45–3:00 PM) and Slot 9 (3:00–3:15 PM), participants transition into the Open Collaboration &amp; Networking window.</li>
            <li>Display America Admin remains on the floor to handle no-shows and overflow requests. Overflow can be manually placed in an Open Collaboration slot in real time; the participant will see it next time they reload the dashboard.</li>
            <li>3:30 PM — Business Matchmaking closes.</li>
          </ol>
        </Block>

        {/* Quick reference */}
        <Block id="quick-ref" label="Quick reference" testId="rb-quickref">
          <div className="grid sm:grid-cols-2 gap-3 text-sm text-zinc-700">
            <RefRow label="Matchmaking Console" value="/admin/matchmaking" />
            <RefRow label="T+L Prime Brief (share link)" value="Admin → Matchmaking tab → Rotate/Copy" />
            <RefRow label="SBE profile edit" value="/sbe/{'{id}'} — magic-link only" />
            <RefRow label="Prime profile edit" value="/dashboard → Company Profile" />
            <RefRow label="Participant itinerary" value="/dashboard (both Prime primary + additional rep + SBE)" />
            <RefRow label="Roll back schedule" value="Console header → Roll Back to Draft" />
          </div>
          <div className="text-xs text-zinc-500 mt-4">
            System uniqueness constraints: table_number 1..8 · round_index 0..9 · one Prime per (time slot) · one SBE per (time slot) · one appointment per (table, time slot). Backend enforces all four with HTTP 409.
          </div>
        </Block>

        {/* Footer */}
        <div className="border-t border-zinc-200 pt-6 text-[11px] uppercase tracking-[0.22em] text-zinc-500 flex items-center gap-2">
          <Sparkles size={12} className="text-[#A39171]" /> Display America · Business Matchmaking Runbook · v1.0
        </div>
      </section>
    </Layout>
  );
}

/* ---------- small presentational helpers ---------- */
function Block({ id, label, children, testId }) {
  return (
    <section id={id} data-testid={testId} className="scroll-mt-16">
      <div className="overline mb-4">{label}</div>
      {children}
    </section>
  );
}
function Sub({ label, children }) {
  return (
    <div className="mt-5">
      <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-2">{label}</div>
      {children}
    </div>
  );
}
function Fact({ label, children }) {
  return (
    <li className="border-l-2 border-[#A39171] pl-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div className="text-sm mt-0.5">{children}</div>
    </li>
  );
}
function RoleRow({ role, text }) {
  return (
    <tr>
      <td className="py-3 pr-4 text-[13px] font-medium align-top whitespace-nowrap">{role}</td>
      <td className="py-3 pr-4 text-[13px] text-zinc-700">{text}</td>
    </tr>
  );
}
function Chip({ color, children }) {
  const cls = {
    emerald: "border-emerald-300 text-emerald-700 bg-emerald-50",
    amber:   "border-amber-300 text-amber-700 bg-amber-50",
    zinc:    "border-zinc-300 text-zinc-700 bg-zinc-50",
  }[color] || "border-zinc-300 text-zinc-700";
  return <span className={`px-1.5 py-0.5 border ${cls}`}>{children}</span>;
}
function Callout({ kind, children }) {
  const styles = kind === "warn"
    ? "border-amber-200 bg-amber-50/50"
    : "border-zinc-200 bg-zinc-50";
  return (
    <div className={`mt-5 border ${styles} p-4 text-sm text-zinc-700`}>
      {children}
    </div>
  );
}
function RefRow({ label, value }) {
  return (
    <div className="border border-zinc-200 p-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div className="text-sm mt-1 font-mono text-zinc-800 break-all">{value}</div>
    </div>
  );
}
